// src/components/Navbar.jsx
import React, { useContext, useState, useEffect } from "react";
import { Disclosure, Menu } from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  SunIcon,
  MoonIcon,
  Cog6ToothIcon,
  DocumentIcon,
  PhotoIcon,
  ViewfinderCircleIcon,
  ChatBubbleLeftIcon,
  CodeBracketIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from "@heroicons/react/24/outline";
import { NavLink } from "react-router-dom";
import { ThemeContext } from "../contexts/ThemeContext";
import { SoundContext } from "../contexts/SoundContext";
import axios from "axios";

const Navbar = ({ setSelectedTopic, selectedTopic }) => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { isMuted, toggleMute } = useContext(SoundContext);
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/list_topics`
        );
        if (response.status === 200) {
          const fetchedTopics = response.data.topics;
          setTopics(fetchedTopics);

          if (fetchedTopics.length > 0) {
            if (!selectedTopic) {
              setSelectedTopic(fetchedTopics[0]);
            } else if (!fetchedTopics.includes(selectedTopic)) {
              setSelectedTopic(fetchedTopics[0]);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching topics:", error);
      }
    };

    fetchTopics();
  }, [setSelectedTopic, selectedTopic]);

  // Updated navLinks array with Heroicons components
  const navLinks = [
    // { name: "Typer", path: "/typer", icon: <FaKeyboard className="h-5 w-5 mr-2" /> },
    {
      name: "Setter",
      path: "/setter",
      icon: <Cog6ToothIcon className="h-5 w-5 mr-2" />,
    },
    {
      name: "Files",
      path: "/files",
      icon: <DocumentIcon className="h-5 w-5 mr-2" />,
    },
    {
      name: "Images",
      path: "/images",
      icon: <PhotoIcon className="h-5 w-5 mr-2" />,
    },
    // { name: "GenAI", path: "/genai", icon: <FaRobot className="h-5 w-5 mr-2" /> },
    {
      name: "Carousel",
      path: "/carousel",
      icon: <ViewfinderCircleIcon className="h-5 w-5 mr-2" />,
    },
    {
      name: "Chat",
      path: "/chat",
      icon: <ChatBubbleLeftIcon className="h-5 w-5 mr-2" />,
    },
    {
      name: "IDE",
      path: "/ide",
      icon: <CodeBracketIcon className="h-5 w-5 mr-2" />,
    },
  ];

  const classNames = (...classes) => classes.filter(Boolean).join(" ");

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
  };

  return (
    <Disclosure
      as="nav"
      className="bg-white dark:bg-gray-800 shadow sticky top-0 z-50"
    >
      {({ open }) => (
        <>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative flex h-16 items-center justify-between">
              {/* Mobile Menu Button */}
              <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white focus:outline-none">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>

              {/* Logo and Title */}
              <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                <div className="flex shrink-0 items-center">
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                    CodeTyper Web
                  </h1>
                </div>

                {/* Navigation Links (Desktop) */}
                <div className="hidden sm:ml-6 sm:block">
                  <div className="flex space-x-4">
                    {navLinks.map((link) => (
                      <NavLink
                        key={link.name}
                        to={link.path}
                        aria-current="page"
                        className={({ isActive }) =>
                          classNames(
                            isActive
                              ? "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white",
                            "flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors"
                          )
                        }
                        title={link.name} // Tooltip for better UX
                      >
                        {link.icon}
                        {link.name}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>

              {/* Theme Toggle, Sound Toggle and Topics Dropdown */}
              <div className="absolute inset-y-0 right-0 flex items-center sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                {/* Sound Toggle Button */}
                <button
                  onClick={toggleMute}
                  className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  aria-label="Toggle Sound"
                  title={
                    isMuted ? "Unmute notifications" : "Mute notifications"
                  }
                >
                  {isMuted ? (
                    <SpeakerXMarkIcon className="h-6 w-6" />
                  ) : (
                    <SpeakerWaveIcon className="h-6 w-6" />
                  )}
                </button>

                {/* Theme Toggle Button */}
                <button
                  onClick={toggleTheme}
                  className="ml-2 p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  aria-label="Toggle Theme"
                >
                  {theme === "light" ? (
                    <MoonIcon className="h-6 w-6" />
                  ) : (
                    <SunIcon className="h-6 w-6" />
                  )}
                </button>

                {/* Topics Dropdown */}
                <Menu as="div" className="relative ml-3">
                  <div>
                    <Menu.Button className="flex items-center text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      <span className="sr-only">Open topics menu</span>
                      <span className="text-gray-700 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400">
                        {selectedTopic || "Topics"}
                      </span>
                      <svg
                        className="ml-1 h-5 w-5 text-gray-500 dark:text-gray-300"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </Menu.Button>
                  </div>
                  <Menu.Items className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg py-1 z-20">
                    {topics.length === 0 ? (
                      <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                        No topics available
                      </div>
                    ) : (
                      topics.map((topic) => (
                        <Menu.Item key={topic}>
                          {({ active }) => (
                            <button
                              onClick={() => handleTopicSelect(topic)}
                              className={classNames(
                                active
                                  ? "bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white"
                                  : "text-gray-700 dark:text-gray-300",
                                "block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                              )}
                            >
                              {topic}
                            </button>
                          )}
                        </Menu.Item>
                      ))
                    )}
                  </Menu.Items>
                </Menu>
              </div>
            </div>
          </div>

          {/* Mobile Menu Links */}
          <Disclosure.Panel className="sm:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navLinks.map((link) => (
                <NavLink
                  key={link.name}
                  to={link.path}
                  className={({ isActive }) =>
                    classNames(
                      isActive
                        ? "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white",
                      "flex items-center px-3 py-2 rounded-md text-base font-medium transition-colors"
                    )
                  }
                  title={link.name} // Tooltip for better UX
                >
                  {link.icon}
                  {link.name}
                </NavLink>
              ))}
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

export default Navbar;
